import { useEffect, useMemo, useState } from "react";
import { useParams as useQueryParams } from "@/hooks/useSearchParams";
import DateSelect from "@/components/DateSelect";
import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import MetricGrid from "@/components/MetricGrid";
import Table, { TableControls, TableHeading } from "@/components/Table";
import { useQuery } from "@apollo/client";
import { useParams, useSearchParams } from "react-router-dom";
import { STUDENT_INTERNSHIPS_QUERY } from "../internships/queries";
import {
  buildInternshipReportingColumns,
  buildInternshipReportingRows,
  buildInternshipReportingSummary
} from "./_utils";

const PAGE_SIZE = 1000;

type DateRange = { from: string; to: string };
type DateFieldValue = { startDate: Date | null; endDate: Date | null } | null;

function toDateInputValue(date: Date | string): string {
  return date instanceof Date ? date.toISOString().split("T")[0] : date.toString();
}

function formatAppliedDateRange(dateRange?: DateRange): string {
  if (!dateRange) return "";
  return `${dateRange.from} to ${dateRange.to}`;
}

export default function UniversityInternshipReportingPage() {
  const { universityId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { params, deleteSearchParams } = useQueryParams();

  const initialDateRange =
    params.startedOnFrom && params.startedOnTo ? { from: params.startedOnFrom, to: params.startedOnTo } : undefined;

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(initialDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState<DateRange | undefined>(initialDateRange);
  const [dateFieldValue, setDateFieldValue] = useState<DateFieldValue>(
    initialDateRange ? { startDate: new Date(initialDateRange.from), endDate: new Date(initialDateRange.to) } : null
  );

  const queryVariables = useMemo(
    () => ({
      first: PAGE_SIZE,
      input: {
        universityId: universityId?.toString() || "",
        startedOnFrom: appliedDateRange?.from,
        startedOnTo: appliedDateRange?.to
      }
    }),
    [appliedDateRange, universityId]
  );

  const { data, loading, fetchMore } = useQuery(STUDENT_INTERNSHIPS_QUERY, {
    variables: queryVariables,
    skip: !universityId || !appliedDateRange,
    notifyOnNetworkStatusChange: true
  });

  const pageInfo = data?.studentInternships?.pageInfo;
  const isFetchingAllPages = !!pageInfo?.hasNextPage;

  useEffect(() => {
    if (!appliedDateRange || !pageInfo?.hasNextPage || !pageInfo.endCursor) return;

    void fetchMore({
      variables: {
        ...queryVariables,
        after: pageInfo.endCursor
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        return {
          studentInternships: {
            ...fetchMoreResult.studentInternships,
            edges: [...previousResult.studentInternships.edges, ...fetchMoreResult.studentInternships.edges]
          }
        };
      }
    });
  }, [appliedDateRange, fetchMore, pageInfo?.endCursor, pageInfo?.hasNextPage, queryVariables]);

  const reportLoading = loading || isFetchingAllPages;

  const reportRows = useMemo(() => buildInternshipReportingRows(data), [data]);
  const reportSummary = useMemo(() => buildInternshipReportingSummary(data), [data]);
  const columns = useMemo(() => buildInternshipReportingColumns(), []);

  const metrics = useMemo(
    () => [
      {
        iconName: "business_center" as const,
        label: "Internships",
        value: reportSummary.internshipCount.toLocaleString()
      },
      {
        iconName: "school" as const,
        label: "Students",
        value: reportSummary.studentCount.toLocaleString()
      },
      {
        iconName: "workspace_premium" as const,
        label: "Total Credits",
        value: reportSummary.totalInternshipCredits.toFixed(1)
      }
    ],
    [reportSummary]
  );

  const handleRunReport = () => {
    if (!selectedDateRange) return;

    setAppliedDateRange(selectedDateRange);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("startedOnFrom", selectedDateRange.from);
    nextSearchParams.set("startedOnTo", selectedDateRange.to);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleClearReport = () => {
    setSelectedDateRange(undefined);
    setAppliedDateRange(undefined);
    setDateFieldValue(null);
    deleteSearchParams(["startedOnFrom", "startedOnTo"]);
  };

  return (
    <>
      <TableHeading
        heading="Internship Reporting"
        body="Report internship totals by facility type for internships that started within a selected date range."
      />
      <div className="mb-4">
        <TableControls>
          <div data-name="dates" data-filter-size="custom">
            <DateSelect
              key={dateFieldValue ? `date-${dateFieldValue.startDate}-${dateFieldValue.endDate}` : "date-empty"}
              label="Internship Start Date Range"
              placeholder="Select Date Range"
              useSingleDate={false}
              useModal={true}
              value={dateFieldValue}
              onChange={(value) => {
                if (typeof value === "object" && value !== null && value.startDate && value.endDate) {
                  const startDate = toDateInputValue(value.startDate);
                  const endDate = toDateInputValue(value.endDate);

                  setDateFieldValue({ startDate: value.startDate as Date, endDate: value.endDate as Date });
                  setSelectedDateRange({ from: startDate, to: endDate });
                } else {
                  handleClearReport();
                }
              }}
            />
          </div>
          <Button onClick={handleRunReport} disabled={!selectedDateRange}>
            Run Report
          </Button>
          {appliedDateRange && (
            <Button variant="text" onClick={handleClearReport}>
              Clear
            </Button>
          )}
        </TableControls>
      </div>
      {appliedDateRange && (
        <section className="mb-6">
          <div className="mb-4">
            <h3 className="text-title-medium text-sys-brand-on-surface">Summary</h3>
            <p className="text-body-small text-sys-brand-on-surface-variant">
              Internships with start dates from {formatAppliedDateRange(appliedDateRange)}.
            </p>
          </div>
          <MetricGrid metrics={metrics} layout="expanded" />
        </section>
      )}
      <section>
        {appliedDateRange && (
          <div className="mb-4">
            <h3 className="text-title-medium text-sys-brand-on-surface">Facility Type Breakdown</h3>
            <p className="text-body-small text-sys-brand-on-surface-variant">
              Counts and internship credits grouped by the internship facility type.
            </p>
          </div>
        )}
        <Table
          data={appliedDateRange ? reportRows : []}
          loading={reportLoading}
          columns={columns}
          data-testid="internship-reporting-table"
          hasActiveFilters={!!appliedDateRange}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="Select a Date Range"
              caption="Choose an internship start date range and run the report."
              iconName="calendar_month"
            />
          )}
          renderFilteredEmptyState={() => (
            <EmptyState
              title="No Internship Results"
              caption="No internships started during the selected date range."
              iconName="business_center"
            />
          )}
        />
      </section>
    </>
  );
}
